import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAccessToken } from "../lib/api";
import { subscribeToPushNotifications } from "../lib/pushSubscription";

const AuthContext = createContext(null);

// Demo accounts wired to the login page quick-buttons.
// Emails + passwords MUST match what `backend/seed.py` inserts.
export const DEMO_ACCOUNTS = {
  sales: { email: "salesman@hondababel.id", password: "Salesman#2026" },
  team_leader: { email: "dealer.asp.pkp@hondababel.id", password: "Dealer#2026" },
  kepala_cabang: { email: "kepala.cabang@hondababel.id", password: "KepCabang#2026" },
  main_dealer: { email: "admin@hondababel.id", password: "HscisAdmin#2026" },
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  // On first mount, try to hydrate session from cookie / stored token.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/auth/me");
        if (alive) {
          setUser(res.data);
          subscribeToPushNotifications(); // attempt subscription if user is hydrated
        }
      } catch {
        if (alive) setUser(null);
      } finally {
        if (alive) setBootstrapping(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      const { user: u, access_token } = res.data;
      if (access_token) setAccessToken(access_token);
      setUser(u);
      subscribeToPushNotifications(); // attempt subscription on explicit login
      return { ok: true, user: u };
    } catch (err) {
      const detail = err?.response?.data?.detail || "Tidak dapat terhubung ke server.";
      return { ok: false, error: detail };
    }
  }, []);

  const loginAsDemo = useCallback(
    async (role) => {
      const creds = DEMO_ACCOUNTS[role];
      if (!creds) return { ok: false, error: "Demo role tidak dikenal." };
      return login(creds.email, creds.password);
    },
    [login]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore — clear local state regardless */
    }
    setAccessToken(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, bootstrapping, login, loginAsDemo, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
